export const NETWORK_DASHBOARD_STYLES = `
/* ── Network Dashboard ── */
.network-dashboard {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  background: var(--bg-primary, white);
  border-radius: 8px;
}

.network-header {
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  padding-bottom: 12px;
}

.network-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary, #1f2937);
}

.network-loading,
.network-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
  text-align: center;
}

.network-loading .spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color, #e5e7eb);
  border-top-color: var(--blue-500, #3b82f6);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.network-error {
  padding: 16px;
  background: var(--red-50, #fef2f2);
  border: 1px solid var(--red-200, #fecaca);
  border-radius: 6px;
  color: var(--red-700, #b91c1c);
  font-size: 14px;
}

/* ── Content Layout ── */
.network-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── Sections ── */
.network-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--bg-secondary, #f9fafb);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 6px;
}

.section-title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, #1f2937);
}

.section-hint {
  font-size: 13px;
  color: var(--text-muted, #6b7280);
  margin-bottom: 8px;
}

/* ── Status Card ── */
.status-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-primary, white);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 4px;
}

.status-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 700;
  flex-shrink: 0;
}

.status-icon.enabled {
  background: var(--green-100, #dcfce7);
  color: var(--green-700, #15803d);
}

.status-icon.disabled {
  background: var(--gray-200, #e5e7eb);
  color: var(--gray-600, #4b5563);
}

.status-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #1f2937);
}

/* ── Warning Box ── */
.warning-box {
  padding: 12px;
  background: var(--yellow-50, #fefce8);
  border: 1px solid var(--yellow-200, #fef08a);
  border-radius: 4px;
  color: var(--yellow-700, #854d0e);
  font-size: 13px;
  margin-top: 8px;
}

/* ── Toggle ── */
.toggle-label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary, #1f2937);
}

.toggle-input {
  width: 44px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

/* ── Categories Grid ── */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.category-item {
  padding: 12px;
  background: var(--bg-primary, white);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 4px;
  transition: all 0.2s;
}

.category-item.enabled {
  background: var(--blue-50, #eff6ff);
  border-color: var(--blue-300, #93c5fd);
}

.category-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  color: var(--text-primary, #1f2937);
}

.category-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: var(--blue-500, #3b82f6);
}

/* ── Domain Input Area ── */
.domain-input-area {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.domain-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 4px;
  font-size: 13px;
  color: var(--text-primary, #1f2937);
}

.domain-input:focus {
  outline: none;
  border-color: var(--blue-500, #3b82f6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.domain-type-select {
  padding: 10px 8px;
  border: 1px solid var(--border-color, #d1d5db);
  border-radius: 4px;
  font-size: 13px;
  background: var(--bg-primary, white);
  color: var(--text-primary, #1f2937);
  cursor: pointer;
}

/* ── Domain Lists ── */
.domain-list {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color, #e5e7eb);
}

.domain-list-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #1f2937);
}

.domain-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.domain-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: var(--bg-primary, white);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 3px;
  font-size: 13px;
}

.domain-name {
  font-family: monospace;
  color: var(--text-primary, #1f2937);
  word-break: break-word;
  flex: 1;
}

/* ── Buttons ── */
.btn {
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary {
  background: var(--blue-500, #3b82f6);
  color: white;
}

.btn-primary:hover {
  background: var(--blue-600, #2563eb);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger {
  background: var(--red-500, #ef4444);
  color: white;
  padding: 4px 8px;
  font-size: 12px;
}

.btn-danger:hover {
  background: var(--red-600, #dc2626);
}

.btn-sm {
  padding: 6px 8px;
  font-size: 12px;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .network-dashboard {
    padding: 16px;
  }

  .domain-input-area {
    flex-direction: column;
  }

  .categories-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
`;
